import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import language from '../../variables/language';

// IANA zone names ("Europe/Warsaw") are what's actually stored/needed (the
// platform side already relies on this exact format for student-timezone-
// aware lesson times) but are useless to pick from as raw strings - this
// derives a readable "City (Region)" label plus the CURRENT utc offset
// (computed live via Intl, so it's always correct across DST rather than a
// stale stored number) for display only.
function describeZone(name) {
   const parts = name.split('/');
   const city = (parts[parts.length - 1] || name).replace(/_/g, ' ');
   const region = parts.length > 1 ? parts[0].replace(/_/g, ' ') : '';
   let offset = '';
   try {
      offset = new Intl.DateTimeFormat('en', { timeZone: name, timeZoneName: 'shortOffset' })
         .formatToParts(new Date()).find((p) => p.type === 'timeZoneName').value.replace('GMT', 'UTC');
   } catch (e) {
      offset = '';
   }
   return { city, region, offset, name };
}

export default function TimezoneSelect(props) {
   const navigate = useNavigate();
   const lang = language[language.lang];
   const [zones, setZones] = useState([]);
   const [status, setStatus] = useState('wait');
   const [open, setOpen] = useState(false);
   const [query, setQuery] = useState('');
   const [highlight, setHighlight] = useState(0);
   const [menuRect, setMenuRect] = useState(null);
   const rootRef = useRef(null);
   const inputRef = useRef(null);
   const menuRef = useRef(null);

   useEffect(() => {
      axios.get(props.url, { withCredentials: true })
         .then((response) => {
            setZones(Array.isArray(response.data) ? response.data.map((z) => ({ id: z.id, ...describeZone(z.name) })) : []);
            setStatus('done');
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('error');
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.url]);

   const selected = useMemo(() => zones.find((z) => String(z.id) === String(props.value)) || null, [zones, props.value]);

   const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return zones;
      return zones.filter((z) => z.city.toLowerCase().includes(q) || z.region.toLowerCase().includes(q)
         || z.name.toLowerCase().includes(q) || z.offset.toLowerCase().replace('utc', '').includes(q.replace('utc', '')));
   }, [zones, query]);

   // The dropdown is portaled to document.body (see render below) - several
   // of this form's field wrappers use backdrop-filter, which creates its
   // own CSS stacking context, so a normal position:absolute child stays
   // trapped behind later sibling fields no matter what z-index it's given.
   // position:fixed + coordinates read straight off the input's own
   // bounding box sidesteps that entirely.
   const updateMenuRect = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width });
   };

   useLayoutEffect(() => {
      if (!open) return;
      updateMenuRect();
      const onReposition = () => updateMenuRect();
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
         window.removeEventListener('scroll', onReposition, true);
         window.removeEventListener('resize', onReposition);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open]);

   useEffect(() => {
      const onClickOutside = (e) => {
         const inRoot = rootRef.current && rootRef.current.contains(e.target);
         const inMenu = menuRef.current && menuRef.current.contains(e.target);
         if (!inRoot && !inMenu) {
            setOpen(false);
            setQuery('');
         }
      };
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
   }, []);

   const choose = (zone) => {
      props.selectorsHundler(zone ? String(zone.id) : '', props.name);
      setOpen(false);
      setQuery('');
   };

   const openDropdown = () => {
      if (props.disabled) return;
      setOpen(true);
      setHighlight(0);
      setTimeout(() => inputRef.current && inputRef.current.select(), 0);
   };

   const handleKeyDown = (e) => {
      if (!open) {
         if (e.key === 'ArrowDown' || e.key === 'Enter') { openDropdown(); e.preventDefault(); }
         return;
      }
      if (e.key === 'ArrowDown') { setHighlight((h) => Math.min(h + 1, filtered.length - 1)); e.preventDefault(); }
      else if (e.key === 'ArrowUp') { setHighlight((h) => Math.max(h - 1, 0)); e.preventDefault(); }
      else if (e.key === 'Enter') { if (filtered[highlight]) choose(filtered[highlight]); e.preventDefault(); }
      else if (e.key === 'Escape') { setOpen(false); setQuery(''); e.preventDefault(); }
   };

   const placeholderLabel = status === 'wait' ? lang['Loading ...'] : lang['not selected'];
   const displayValue = open ? query : (selected ? selected.city + ' (' + selected.region + ') · ' + selected.offset : '');

   const menu = open && menuRect ? createPortal(
      <div className='timezone-select-dropdown' ref={menuRef}
         style={{ position: 'fixed', top: menuRect.top, left: menuRect.left, width: menuRect.width }}>
         {filtered.length === 0 ? (
            <div className='timezone-select-empty'>{lang['Empty list']}</div>
         ) : filtered.map((zone, index) => (
            <div key={zone.id}
               className={'timezone-select-option' + (index === highlight ? ' active' : '') + (selected && selected.id === zone.id ? ' selected' : '')}
               onMouseEnter={() => setHighlight(index)}
               onMouseDown={(e) => { e.preventDefault(); choose(zone); }}>
               <span className='timezone-select-city'>{zone.city}</span>
               <span className='timezone-select-region'>{zone.region}</span>
               <span className='timezone-select-offset'>{zone.offset}</span>
            </div>
         ))}
      </div>,
      document.body
   ) : null;

   return (
      <div className='timezone-select' ref={rootRef}>
         <input ref={inputRef} type='text' className='submit-field form-selection txt-c timezone-select-input'
            placeholder={placeholderLabel}
            value={displayValue}
            disabled={props.disabled}
            onFocus={openDropdown}
            onClick={openDropdown}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
            onKeyDown={handleKeyDown} />
         {selected && !open ? (
            <div className='timezone-select-clear' title={lang.timeZoneClear} onClick={() => choose(null)}>×</div>
         ) : ''}
         {menu}
      </div>
   );
}
